import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { i18n, initI18n, DEFAULT_LOCALE, SupportedLocale, SUPPORTED_LOCALES } from './index';

interface LocaleContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  isI18nEnabled: boolean;
  isLoading: boolean;
  isReady: boolean;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  isI18nEnabled: false,
  isLoading: false,
  isReady: true,
});

interface LocaleProviderProps {
  children: ReactNode;
  enabled?: boolean;
  defaultLocale?: SupportedLocale;
}

export function LocaleProvider({ 
  children, 
  enabled = false,
  defaultLocale = DEFAULT_LOCALE 
}: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(defaultLocale);
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    initI18n(enabled)
      .then(() => {
        if (enabled) {
          const detectedLocale = i18n.language as SupportedLocale;
          if (SUPPORTED_LOCALES.includes(detectedLocale)) {
            setLocaleState(detectedLocale);
          }
        }
        setIsLoading(false);
        setIsReady(true);
      })
      .catch((error) => {
        console.error('Failed to initialize i18n:', error);
        setIsLoading(false);
        setIsReady(true);
      });
  }, [enabled]);

  const setLocale = (newLocale: SupportedLocale) => {
    if (!enabled) return;
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setLocaleState(newLocale);
      i18n.changeLanguage(newLocale);
      localStorage.setItem('app_locale', newLocale);
    }
  };

  const value: LocaleContextValue = {
    locale,
    setLocale,
    isI18nEnabled: enabled,
    isLoading,
    isReady,
  };

  if (!isReady) return null;

  return (
    <LocaleContext.Provider value={value}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LocaleContext.Provider>
  );
}

export function useLocaleContext() { return useContext(LocaleContext); }
export { LocaleContext };
